
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { createInvoice } from "../../../../../services/Admin/InvoicesApi";
import { handleBrowserPrintAfterPay } from "../PrintOrder/PrinAfter/printUtilsAfterPay";
import { normalizeOrderForPrint } from "../PrintOrder/printNormalizer";

const OrderInvoicePayment = ({
  order,
  totalItemsValue,
  onInvoiceCreated,
  onCalculationsChange,
  productsMap,
}) => {
  // ✅ Flag لمنع الكتابة في localStorage أثناء التهيئة (hydration)
  const hydrationCompleted = useRef(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    service_input: 0,
    service_mode: "amount",
    delivery_service_input: 0,
    delivery_service_mode: "amount",
    tax_input: 0,
    tax_mode: "amount",
    discount_input: 0,
    discount_mode: "amount",
  });

  const [calculatedValues, setCalculatedValues] = useState({
    sub_total: 0,
    service: 0,
    delivery_service: 0,
    tax: 0,
    discount: 0,
    service_ratio: 0,
    delivery_service_ratio: 0,
    tax_ratio: 0,
    discount_ratio: 0,
    total_amount: 0,
  });

  const getAmountFromInput = (value, mode, subTotal) => {
    const parsedValue = Number(value) || 0;
    if (mode === "amount") return parsedValue;
    return (subTotal * parsedValue) / 100;
  };

  const getRatioFromAmount = (amount, subTotal) => {
    if (!subTotal) return 0;
    return (amount / subTotal) * 100;
  };

  const parsedItemsTotal = Number(totalItemsValue);
  const subTotalValue = Number.isFinite(parsedItemsTotal)
    ? parsedItemsTotal
    : parseFloat(order?.total) || 0;
  const isUnderReview = order?.status === "under_review";

  // ✅ HYDRATION عند تغيير order.id (بدون Dialog)
  useEffect(() => {
    if (!order?.id) return;

    hydrationCompleted.current = false;

    const savedInvoiceData = localStorage.getItem(`invoiceCalculations_${order.id}`);
    let savedData = null;

    if (savedInvoiceData) {
      try {
        savedData = JSON.parse(savedInvoiceData);
      } catch (e) {
        console.warn("Failed to parse saved invoice data");
      }
    }

    const newFormData = {
      service_input: savedData?.service_input !== undefined ? savedData.service_input : 0,
      service_mode: savedData?.service_mode || "amount",
      delivery_service_input:
        savedData?.delivery_service_input !== undefined ? savedData.delivery_service_input : 0,
      delivery_service_mode: savedData?.delivery_service_mode || "amount",
      tax_input: savedData?.tax_input !== undefined ? savedData.tax_input : 0,
      tax_mode: savedData?.tax_mode || "amount",
      discount_input: savedData?.discount_input !== undefined ? savedData.discount_input : 0,
      discount_mode: savedData?.discount_mode || "amount",
    };

    setFormData(newFormData);
    setError("");
    setSuccess(false);

    // حساب القيم الابتدائية بناءً على البيانات المحملة
    const subTotal = subTotalValue;

    const service = getAmountFromInput(newFormData.service_input, newFormData.service_mode, subTotal);
    const deliveryService = getAmountFromInput(
      newFormData.delivery_service_input,
      newFormData.delivery_service_mode,
      subTotal
    );
    const tax = getAmountFromInput(newFormData.tax_input, newFormData.tax_mode, subTotal);
    const discount = getAmountFromInput(
      newFormData.discount_input,
      newFormData.discount_mode,
      subTotal
    );
    const totalAmount = subTotal + service + deliveryService + tax - discount;

    setCalculatedValues({
      sub_total: subTotal,
      service: parseFloat(service.toFixed(2)),
      delivery_service: parseFloat(deliveryService.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      discount: parseFloat(discount.toFixed(2)),
      service_ratio: parseFloat(getRatioFromAmount(service, subTotal).toFixed(2)),
      delivery_service_ratio: parseFloat(getRatioFromAmount(deliveryService, subTotal).toFixed(2)),
      tax_ratio: parseFloat(getRatioFromAmount(tax, subTotal).toFixed(2)),
      discount_ratio: parseFloat(getRatioFromAmount(discount, subTotal).toFixed(2)),
      total_amount: parseFloat(totalAmount.toFixed(2)),
    });

    queueMicrotask(() => {
      hydrationCompleted.current = true;
    });
  }, [order?.id, subTotalValue]);

  // ✅ إعادة حساب القيم عند تغيير inputs (لكن بعد انتهاء hydration)
  useEffect(() => {
    if (!order?.id || !hydrationCompleted.current) return;

    const subTotal = subTotalValue;

    const service = getAmountFromInput(formData.service_input, formData.service_mode, subTotal);
    const deliveryService = getAmountFromInput(
      formData.delivery_service_input,
      formData.delivery_service_mode,
      subTotal
    );
    const tax = getAmountFromInput(formData.tax_input, formData.tax_mode, subTotal);
    const discount = getAmountFromInput(formData.discount_input, formData.discount_mode, subTotal);

    const totalAmount = subTotal + service + deliveryService + tax - discount;

    setCalculatedValues({
      sub_total: subTotal,
      service: parseFloat(service.toFixed(2)),
      delivery_service: parseFloat(deliveryService.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      discount: parseFloat(discount.toFixed(2)),
      service_ratio: parseFloat(getRatioFromAmount(service, subTotal).toFixed(2)),
      delivery_service_ratio: parseFloat(getRatioFromAmount(deliveryService, subTotal).toFixed(2)),
      tax_ratio: parseFloat(getRatioFromAmount(tax, subTotal).toFixed(2)),
      discount_ratio: parseFloat(getRatioFromAmount(discount, subTotal).toFixed(2)),
      total_amount: parseFloat(totalAmount.toFixed(2)),
    });
  }, [formData, subTotalValue, order?.id]);

  // ✅ حفظ في localStorage فقط بعد انتهاء hydration وعند تغيير المستخدم
  useEffect(() => {
    if (!order?.id || !hydrationCompleted.current) return;

    const invoiceData = {
      service_input: formData.service_input,
      service_mode: formData.service_mode,
      delivery_service_input: formData.delivery_service_input,
      delivery_service_mode: formData.delivery_service_mode,
      tax_input: formData.tax_input,
      tax_mode: formData.tax_mode,
      discount_input: formData.discount_input,
      discount_mode: formData.discount_mode,
      total_amount: calculatedValues.total_amount,
    };

    localStorage.setItem(`invoiceCalculations_${order.id}`, JSON.stringify(invoiceData));
  }, [formData, calculatedValues.total_amount, order?.id]);

  // ✅ إرسال بيانات الحسابات الحالية للأب (TableOrderInfo) لاستخدامها في الطباعة المباشرة
  useEffect(() => {
    if (!order?.id || typeof onCalculationsChange !== "function") return;

    onCalculationsChange({
      service: calculatedValues.service,
      service_ratio: calculatedValues.service_ratio,
      service_is_percentage: formData.service_mode === "ratio" ? 1 : 0,
      delivery_service: calculatedValues.delivery_service,
      delivery_service_ratio: calculatedValues.delivery_service_ratio,
      delivery_service_is_percentage: formData.delivery_service_mode === "ratio" ? 1 : 0,
      tax: calculatedValues.tax,
      tax_ratio: calculatedValues.tax_ratio,
      tax_is_percentage: formData.tax_mode === "ratio" ? 1 : 0,
      discount: calculatedValues.discount,
      discount_ratio: calculatedValues.discount_ratio,
      discount_is_percentage: formData.discount_mode === "ratio" ? 1 : 0,
      sub_total: calculatedValues.sub_total,
      total_amount: calculatedValues.total_amount,
    });
  }, [calculatedValues, formData, order?.id, onCalculationsChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const validateForm = () => {
    if (
      Number(formData.service_input) < 0 ||
      Number(formData.delivery_service_input) < 0 ||
      Number(formData.tax_input) < 0 ||
      Number(formData.discount_input) < 0
    ) {
      setError("القيم يجب أن تكون أكبر من أو تساوي صفر");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!order?.id) return;
    if (isUnderReview) return;
    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const todayDate = new Date().toISOString().split("T")[0];

      const invoiceData = {
        order_id: order.id,
        service: calculatedValues.service,
        delivery_service: calculatedValues.delivery_service,
        tax: calculatedValues.tax,
        discount: calculatedValues.discount,
        service_ratio: calculatedValues.service_ratio,
        delivery_service_ratio: calculatedValues.delivery_service_ratio,
        tax_ratio: calculatedValues.tax_ratio,
        discount_ratio: calculatedValues.discount_ratio,
        sub_total: calculatedValues.sub_total,
        total_amount: calculatedValues.total_amount,
        date: todayDate,
        service_is_percentage: formData.service_mode === "ratio" ? 1 : 0,
        delivery_service_is_percentage: formData.delivery_service_mode === "ratio" ? 1 : 0,
        tax_is_percentage: formData.tax_mode === "ratio" ? 1 : 0,
        discount_is_percentage: formData.discount_mode === "ratio" ? 1 : 0,
      };

      // ✅ طباعة الفاتورة قبل الحفظ
      try {
const orderForPrint = normalizeOrderForPrint(order, totalItemsValue, productsMap || {}, invoiceData);
await handleBrowserPrintAfterPay(orderForPrint, invoiceData, order.id);      } catch (printError) {
        console.warn("خطأ في طباعة الفاتورة:", printError);
        // لا نوقف عملية الحفظ إذا فشلت الطباعة
      }

      const response = await createInvoice(invoiceData);

      setSuccess(true);
      localStorage.removeItem(`invoiceCalculations_${order.id}`);

      onInvoiceCreated && onInvoiceCreated(response);
    } catch (err) {
      console.error("خطأ في إنشاء الفاتورة:", err);
      setError(err.message || "حدث خطأ في إنشاء الفاتورة");
    } finally {
      setLoading(false);
    }
  };

  if (!order?.id) return null;

  return (
    <Paper sx={{ p: 2 }} dir="rtl">
      <Typography sx={{ fontWeight: "bold", textAlign: "right", mb: 1 }}>
        إنشاء فاتورة للطلب #{order.id}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          تم إنشاء الفاتورة بنجاح
        </Alert>
      )}

      <Divider sx={{ my: 2 }}>
        <Typography variant="subtitle2" color="textSecondary">
          حسابات الفاتورة
        </Typography>
      </Divider>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Grid container spacing={2}>
          {/* الخدمة */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                label={formData.service_mode === "ratio" ? "نسبة الخدمة (%)" : "قيمة الخدمة"}
                name="service_input"
                type="number"
                value={formData.service_input}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={loading}
                inputProps={{ min: 0, step: 0.1 }}
              />
              <FormControlLabel
                sx={{ m: 0, whiteSpace: "nowrap" }}
                control={
                  <Switch
                    checked={formData.service_mode === "ratio"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        service_mode: e.target.checked ? "ratio" : "amount",
                      }))
                    }
                    disabled={loading}
                  />
                }
                label={formData.service_mode === "ratio" ? "%" : "قيمة"}
              />
            </Box>
          </Grid>

          {/* التوصيل */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                label={
                  formData.delivery_service_mode === "ratio"
                    ? "نسبة التوصيل (%)"
                    : "قيمة التوصيل"
                }
                name="delivery_service_input"
                type="number"
                value={formData.delivery_service_input}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={loading}
                inputProps={{ min: 0, step: 0.1 }}
              />
              <FormControlLabel
                sx={{ m: 0, whiteSpace: "nowrap" }}
                control={
                  <Switch
                    checked={formData.delivery_service_mode === "ratio"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        delivery_service_mode: e.target.checked ? "ratio" : "amount",
                      }))
                    }
                    disabled={loading}
                  />
                }
                label={formData.delivery_service_mode === "ratio" ? "%" : "قيمة"}
              />
            </Box>
          </Grid>

          {/* الضريبة */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                label={formData.tax_mode === "ratio" ? "نسبة الضريبة (%)" : "قيمة الضريبة"}
                name="tax_input"
                type="number"
                value={formData.tax_input}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={loading}
                inputProps={{ min: 0, step: 0.1 }}
              />
              <FormControlLabel
                sx={{ m: 0, whiteSpace: "nowrap" }}
                control={
                  <Switch
                    checked={formData.tax_mode === "ratio"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        tax_mode: e.target.checked ? "ratio" : "amount",
                      }))
                    }
                    disabled={loading}
                  />
                }
                label={formData.tax_mode === "ratio" ? "%" : "قيمة"}
              />
            </Box>
          </Grid>

          {/* الخصم */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                label={formData.discount_mode === "ratio" ? "نسبة الخصم (%)" : "قيمة الخصم"}
                name="discount_input"
                type="number"
                value={formData.discount_input}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={loading}
                inputProps={{ min: 0, step: 0.1 }}
              />
              <FormControlLabel
                sx={{ m: 0, whiteSpace: "nowrap" }}
                control={
                  <Switch
                    checked={formData.discount_mode === "ratio"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        discount_mode: e.target.checked ? "ratio" : "amount",
                      }))
                    }
                    disabled={loading}
                  />
                }
                label={formData.discount_mode === "ratio" ? "%" : "قيمة"}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Summary Box */}
        <Paper sx={{ p: 2, backgroundColor: "#e3f2fd", border: "1px solid #2196f3" }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
          >
            ملخص الفاتورة:
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Row label="الإجمالي الفرعي" value={calculatedValues.sub_total} />

            {calculatedValues.service > 0 && (
              <Row
                label={`+ رسوم الخدمة (${calculatedValues.service_ratio}%)`}
                value={calculatedValues.service}
                color="success.main"
              />
            )}

            {calculatedValues.delivery_service > 0 && (
              <Row
                label={`+ رسوم التوصيل (${calculatedValues.delivery_service_ratio}%)`}
                value={calculatedValues.delivery_service}
                color="success.main"
              />
            )}

            {calculatedValues.tax > 0 && (
              <Row
                label={`+ الضريبة (${calculatedValues.tax_ratio}%)`}
                value={calculatedValues.tax}
                color="success.main"
              />
            )}

            {calculatedValues.discount > 0 && (
              <Row
                label={`- الخصم (${calculatedValues.discount_ratio}%)`}
                value={calculatedValues.discount}
                color="error.main"
              />
            )}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                الإجمالي النهائي:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
                {calculatedValues.total_amount.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="success"
            disabled={loading || isUnderReview}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {loading && <CircularProgress size={20} color="inherit" />}
            {loading ? "جاري إنشاء الفاتورة..." : "حفظ وطباعة الفاتورة"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

const Row = ({ label, value, color }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
    <Typography variant="body2" sx={{ color: color || "inherit" }}>
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: "bold", color: color || "inherit" }}>
      {Number(value || 0).toFixed(2)}
    </Typography>
  </Box>
);

export default OrderInvoicePayment;